import React, { useEffect, useState } from 'react';
import {
  Grid, Box, CircularProgress,
} from '@mui/material';

import SiteInfoTable from './SiteInfoTable';
import { axiosErrorToUserMessage } from '../services/axios';
import Toast, { useToast } from '../components/useToast';
import CustomAppBar from '../content/Appbar/CustomAppBar';
import { useApiClient } from '../context/ApiClientContext';
import basicServiceHandler from '../services/basicServiceHandler';
import CreateNewSiteButton from '../content/CreateNewSiteButton';
import SearchBar from '../components/SearchBar';
import useSiteSettings from '../hooks/useSiteSettings';
import usePermission from '../hooks/usePermissions';

const getIntakePlansForSite = (site, intakePlanMap) => {
  const intakePlansSettingValue = site.settings.find((setting) => setting.name === 'intakePlans')?.value;
  if (!intakePlansSettingValue) return [];
  if (intakePlansSettingValue === '*') return '*';
  return intakePlansSettingValue.split(',').map((id) => intakePlanMap[id]);
};

export default function SiteFrontend() {
  const { accessibleSiteInfos } = useSiteSettings();

  const [siteData, setSiteData] = useState([]);
  const [searchSite, setSearchSite] = useState('');
  const [filteredSites, setFilteredSites] = useState(siteData);
  const [loading, setLoading] = useState(true);

  const apiClient = useApiClient();
  const {
    toastState,
    toastSetSuccessNotification,
    toastSetErrorNotification,
    toastOnClose,
  } = useToast();

  const { isAdmin } = usePermission();

  const fetchSiteData = () => {
    basicServiceHandler(
      () => apiClient.getAllSite(),
      async (result) => {
        const allIntakePlans = await apiClient.getIntakePlans();
        const intakePlansMap = allIntakePlans.data.reduce((acc, intakePlan) => {
          acc[intakePlan.id] = intakePlan;
          return acc;
        }, {});
        const newSiteData = result.data.map((site) => ({
          ...site,
          intakePlans: getIntakePlansForSite(site, intakePlansMap),
        }));
        setSiteData(newSiteData);
        setFilteredSites(newSiteData);
        setLoading(false);
      },
      (error) => {
        toastSetErrorNotification(
          axiosErrorToUserMessage(error) || 'Error fetching sites.',
        );
        setLoading(false);
      },
    );
  };

  const handleSearchInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchSite(inputValue);

    const filteredData = siteData
      .filter((site) => site.name.toLowerCase().includes(inputValue.toLowerCase()));
    setFilteredSites(filteredData);
  };
  useEffect(() => {
    if (accessibleSiteInfos.length > 0) {
      fetchSiteData();
    }
  }, [accessibleSiteInfos]);

  return (
    <Box sx={{ height: '100vh' }}>
      <Grid container>
        <Grid sx={{ width: 1 }}>
          <CustomAppBar
            siteSelectorOn={false}
            name="Site Settings Page"
          />
        </Grid>
        <Grid sx={{ width: 1, padding: '0 30px' }}>
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem',
          }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
            }}
          >
            <SearchBar
              value={searchSite}
              onChange={handleSearchInputChange}
              placeholder="Search by Site Name"
            />
            <CreateNewSiteButton
              setErrorMessage={toastSetErrorNotification}
              setSuccessMessage={toastSetSuccessNotification}
              refreshParent={fetchSiteData}
            />
          </Box>
          {loading ? (
            <Box sx={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%',
            }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <SiteInfoTable
              isAdmin={isAdmin()}
              data={filteredSites}
              setErrorMessage={toastSetErrorNotification}
              setSuccessMessage={toastSetSuccessNotification}
              refreshParent={fetchSiteData}
            />
          )}
          <Toast
            open={toastState.open}
            severity={toastState.severity}
            text={toastState.text}
            onClose={toastOnClose}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
